import React from "react";
import styled from "styled-components";
import EmptyState from "../EmptyState";
import { isEmpty } from "../../util/util";
import DetailsComponent from "./DetailsComponent";
import { ContentComponent, ContentContainer } from "./ContentComponents";

export const DataTypes = {
	Object: "Object",
	List: "List",
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: max-content;
	width: 100%;
	gap: 1.5rem;
`;

/**
 * Walks a declarative details config and renders DetailsComponent sections.
 * Same semantics as the former OrderDetails parseConfig helper.
 */
export function parseDetailsConfig(config, lookupData) {
	if (!config) {
		return <EmptyState message="No config Provided" />;
	}

	let renderData = [];

	for (const group in config) {
		const {
			disableCollapse,
			initiallyExpanded,
			title,
			type,
			skipIfEmpty,
			children,
			retrieveListData,
			errored,
		} = config[group];

		switch (type) {
			case DataTypes.List: {
				let listGroupData = [],
					data;

				try {
					data = retrieveListData(lookupData);
				} catch (error) {
					console.log(
						`Could not render group ${group} due to the following issue in getting listdata : ${error.message}`
					);
				}

				if (isEmpty(data) && skipIfEmpty) continue;

				for (const dataIndex in data) {
					let rowData = [],
						rowLookup = data[dataIndex],
						isErrored = false;

					if (errored) {
						try {
							const erroredValue = errored(rowLookup);
							if (erroredValue) {
								isErrored = true;
							}
						} catch (error) {
							console.log(
								`Could not render child with title ${title} of group ${group} due to error : ${error.message}`
							);
						}
					}

					for (const index in children) {
						const { title: childTitle, value, render, omit: checkOmitCondition } =
							children[index];

						if (checkOmitCondition) {
							try {
								const omit = checkOmitCondition(rowLookup);
								if (omit) continue;
							} catch (error) {
								console.log(
									`Could not check omit condition for child with index ${index} of group ${group} due to error : ${error.message}`
								);
								continue;
							}
						}

						if (render) {
							try {
								rowData.push(render(rowLookup, lookupData, index, dataIndex));
							} catch (error) {
								console.log(
									`Could not render child with index ${index} of group ${group} due to error : ${error.message}`
								);
							}
						} else {
							let displayValue = "-";
							try {
								displayValue = value(rowLookup, lookupData, index, dataIndex);
							} catch (error) {
								console.log(
									`Could not render child with title ${childTitle} of group ${group} due to error : ${error.message}`
								);
							}

							rowData.push(
								<ContentComponent
									key={`${group}-${index}`}
									title={childTitle}
									value={displayValue}
								/>
							);
						}
					}

					listGroupData.push(
						<ContentContainer isErrored={isErrored} key={`${group}-${dataIndex}`}>
							{rowData}
						</ContentContainer>
					);
				}

				renderData.push(
					<DetailsComponent
						isList={true}
						key={group}
						disableCollapse={disableCollapse}
						initiallyExpanded={initiallyExpanded}
						title={title}
					>
						{listGroupData}
					</DetailsComponent>
				);

				break;
			}

			case DataTypes.Object:
			default: {
				let groupData = [];

				for (const index in children) {
					const { title: childTitle, value, render, omit: checkOmitCondition } =
						children[index];

					if (checkOmitCondition) {
						try {
							const omit = checkOmitCondition(lookupData);
							if (omit) continue;
						} catch (error) {
							console.log(
								`Could not check omit condition for child with index ${index} of group ${group} due to error : ${error.message}`
							);
							continue;
						}
					}

					if (render) {
						try {
							groupData.push(render(lookupData));
						} catch (error) {
							console.log(
								`Could not render child with index ${index} of group ${group} due to error : ${error.message}`
							);
						}
					} else {
						let displayValue = "-";

						try {
							displayValue = value(lookupData);
						} catch (error) {
							console.log(
								`Could not render child with title ${childTitle} of group ${group} due to error : ${error.message}`
							);
						}

						groupData.push(
							<ContentComponent
								key={`${group}-${index}`}
								title={childTitle}
								value={displayValue}
							/>
						);
					}
				}

				renderData.push(
					<DetailsComponent
						key={group}
						disableCollapse={disableCollapse}
						initiallyExpanded={initiallyExpanded}
						title={title}
					>
						{groupData}
					</DetailsComponent>
				);
			}
		}
	}

	return <Container>{renderData}</Container>;
}
